<!--
@component
Logo

See markup below.
-->

<script lang="ts">
  import getPath from '$utils/getPath';
  import settings from '$lib/config/settings.json';
  import { page } from '$app/state';

  // Tournament-specific labels (name / year / iconFile / etc.) come from
  // content (`content.tournamment[active]`). The active key is a one-time
  // build setting in `src/lib/config/settings.json` — read directly here
  // rather than threading through reactive state.
  const tournament = settings.tournament.type as 'wc' | 'euro';
  const label = $derived(page.data?.content?.tournamment?.[tournament]);
  const iconFile = $derived(label?.iconFile);
</script>

{#if tournament === 'wc'}
  <div class="logo logo-wc">
    <div class="wc-row">
      <div class="wc-text leading-none">
        {label?.name}
      </div>
      <img
        class="wc-trophy mx-auto fmy-0"
        src={getPath(`images/icons/${iconFile}`)}
        width="100"
        height="160"
        alt=""
      />
      <div class="wc-year leading-none">{label?.year}</div>
    </div>
  </div>
{:else}
  <div class="logo logo-euro">
    <img
      class="euro-trophy mx-auto fmy-0"
      src={getPath(`images/icons/${iconFile}`)}
      width="100"
      height="160"
      alt=""
    />
    <div class="euro-hed">
      <div class="euro-custom-hed flex mx-auto flex-col">
        <span class="euro-title leading-none text-secondary"
          ><span class="euro-name">{label?.name}</span>
          {label?.year}</span
        >
      </div>
    </div>
  </div>
{/if}

<svelte:head>
  <link rel="preload" as="image" href={getPath(`images/icons/${iconFile}`)} />
</svelte:head>

<style lang="scss">
  @use '../../../styles/mixins' as mixins;

  .logo {
    user-select: none;
  }

  /* EURO specific styles */
  .euro-trophy {
    @include mixins.hard-shadow;
    width: var(--theme-font-size-5xl);
    height: auto;
    transform: translateX(80%);
  }

  .euro-hed {
    font-optical-sizing: auto;
    @include mixins.font-bold;
    font-style: normal;
    color: var(--theme-colour-text-primary);
    font-family: var(--theme-font-family-hed);
    font-size: var(--theme-font-size-5xl);
    @include mixins.leading-none;
    @include mixins.fmy-1;
  }

  .euro-custom-hed {
    width: fit-content;
    position: relative;
  }

  .euro-title {
    position: absolute;
    text-align: center;
    left: 50%;
    transform: translateX(-50%);
    width: calc(5 * var(--theme-font-size-5xl));
    top: calc(-1.4 * var(--theme-font-size-5xl));

    .euro-name {
      color: var(--theme-colour-logo);
      margin-right: clamp(1.25rem, 0.8158rem + 2.1053vw, 2.5rem);
    }
  }

  /* World Cup specific styles */
  .logo-wc {
    font-optical-sizing: auto;
    @include mixins.font-bold;
    font-family: var(--theme-font-family-hed);
    color: var(--theme-colour-text-primary);
  }

  .wc-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0;
    width: fit-content;
    margin: 0 auto;
  }

  .wc-text {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    text-align: end;
    width: calc(3.5 * var(--theme-font-size-5xl));
    color: var(--theme-colour-logo);
    font-size: calc(0.875 * var(--theme-font-size-5xl));
    font-weight: 800;
    line-height: 0.875;
    margin-inline-start: -3rem;
  }

  .wc-trophy {
    @include mixins.hard-shadow;
    width: auto;
    height: calc(2 * var(--theme-font-size-5xl));
    display: block;
    margin-inline-start: calc(-3 * clamp(0.31rem, 0.31rem + 0vw, 0.31rem));
    margin-inline-end: calc(-11 * clamp(0.31rem, 0.31rem + 0vw, 0.31rem));

    @media (max-width: mixins.$column-width-wide) {
      margin-inline-end: calc(-9 * clamp(0.31rem, 0.31rem + 0vw, 0.31rem));
    }
    @media (max-width: mixins.$column-width-normal) {
      margin-inline-start: calc(-2 * clamp(0.31rem, 0.31rem + 0vw, 0.31rem));
      margin-inline-end: calc(-7 * clamp(0.31rem, 0.31rem + 0vw, 0.31rem));
    }
    @media (max-width: mixins.$column-width-narrow) {
      margin-inline-start: calc(-1 * clamp(0.31rem, 0.31rem + 0vw, 0.31rem));
      margin-inline-end: calc(-5 * clamp(0.31rem, 0.31rem + 0vw, 0.31rem));
    }
  }

  .wc-year {
    color: var(--theme-style-trophy-fill-colour);
    font-size: calc(2 * var(--theme-font-size-5xl));
    @include mixins.font-medium;
    @include mixins.leading-none;
    font-variant-numeric: tabular-nums;
    letter-spacing: calc(-0.25 * var(--theme-font-size-5xl));
  }
</style>
